import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchBranches } from '../branchesApi';
import {
  createBoardMember,
  fetchBoardMemberById,
  mapDirectorFormToPayload,
  updateBoardMember,
} from '../boardApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './BoardPage.css';
import './DecisionFormPage.css';

const EMPTY = {
  name: '',
  branchId: '',
  status: 'active',
  nationality: '',
  nationalId: '',
  birthDate: '',
  membershipType: '',
  membershipStartDate: '',
  membershipEndDate: '',
  partnerKind: '',
  photoData: '',
  userName: '',
  password: '',
  phone: '',
  email: '',
};

const MEMBERSHIP_TYPES = [
  { value: 'رئيس مجلس الإدارة', key: 'directors.typeChairman' },
  { value: 'نائب رئيس مجلس الإدارة', key: 'directors.typeVice' },
  { value: 'عضو تنفيذي', key: 'directors.typeExecutive' },
  { value: 'عضو غير تنفيذي', key: 'directors.typeNonExecutive' },
  { value: 'عضو مستقل', key: 'directors.typeIndependent' },
];

export default function DirectorFormPage({ mode }) {
  const { direction } = useLanguage();
  const t = useT();

  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const { id: idParam } = useParams();
  const isNew = mode === 'new';
  const editId = isNew ? null : Number(idParam);

  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setLoadError(t('common.noPerm')); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setLoadError('');
      try {
        const companyRows = await fetchBranches();
        if (cancelled) return;
        setBranches(companyRows);
        if (isNew) {
          setForm({ ...EMPTY, branchId: companyRows[0]?.id ? String(companyRows[0].id) : '' });
        } else {
          const row = await fetchBoardMemberById(editId);
          if (cancelled) return;
          setForm({
            name: row.name,
            branchId: row.branchId ? String(row.branchId) : '',
            status: row.status,
            nationality: row.nationality,
            nationalId: row.nationalId,
            birthDate: row.birthDate,
            membershipType: row.membershipType,
            membershipStartDate: row.membershipStartDate,
            membershipEndDate: row.membershipEndDate,
            partnerKind: row.partnerKind,
            photoData: row.photoData,
            userName: row.userName,
            password: '',
            phone: row.phone,
            email: row.email,
          });
        }
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('directors.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, isNew, editId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    setErrors((p) => ({ ...p, [e.target.name]: '', _api: '' }));
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, photoData: String(reader.result || '') }));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const validate = () => {
    const errs = {};
    if (!form.name?.trim()) errs.name = t('common.required');
    if (!form.branchId) errs.branchId = t('common.required');
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const fillSample = () => {
    setErrors({});
    const year = new Date().getFullYear();
    setForm((p) => ({
      ...p,
      name: 'م. خالد عبدالعزيز الراشد — عينة',
      nationality: 'سعودي',
      nationalId: '1098765432',
      birthDate: '1978-04-15',
      membershipType: 'عضو تنفيذي',
      membershipStartDate: `${year}-01-01`,
      membershipEndDate: `${year + 3}-12-31`,
      partnerKind: 'مساهم',
      phone: '+966501234567',
      email: 'khaled.alrashed@example.com',
    }));
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = mapDirectorFormToPayload(form);
      if (isNew) await createBoardMember(payload);
      else await updateBoardMember(editId, payload);
      navigate(`${basePath}/board`);
    } catch (e) {
      setErrors({ _api: e instanceof Error ? e.message : t('directors.saveFailed') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="decision-v2 decision-v2--layout-full" dir={direction}>
      <div className="page-wrapper">
        {loadError && <div className="v2-error">{loadError}</div>}
        {loading && <div className="v2-loading">{t('directors.loading')}</div>}
        {!loading && !loadError && (
          <div className="card">
            <div className="card-top">
              <div className="top-bar top-bar--single-line">
                <div className="page-title-row">
                  <h1 className="page-title">{isNew ? t('directors.newTitle') : t('directors.editTitle')}<span className="page-title-icon"><Icon name="board" size={16} color="#0EA5A0" /></span></h1>
                </div>
                <div className="action-btns">
                  <button type="button" className="btn btn-outline" onClick={fillSample}>{t('branches.fillSample')}</button>
                  <button type="button" className="btn btn-ghost" onClick={() => navigate(`${basePath}/board`)}>{t('common.cancel')}</button>
                  <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? '…' : t('common.save')}</button>
                </div>
              </div>
              <p style={{ margin: '4px 0 0', color: '#607d8b', fontSize: '0.88rem' }}>{t('directors.votesNote')}</p>
            </div>
            <div className="card-inner">
              <div className="mform">
                {errors._api && <div style={{ marginBottom: 12, padding: '10px 12px', background: '#ffebee', color: '#c62828', borderRadius: 8 }}>{errors._api}</div>}

                {/* الصورة */}
                <div className="mform__row mform__row--4">
                  <div className="mform__field mform__field--span4" style={{ alignItems: 'center', display: 'flex', gap: 16 }}>
                    {form.photoData
                      ? <img src={form.photoData} alt="" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e0f2f1' }} />
                      : <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#eceff1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="user" size={28} color="#90a4ae" /></div>}
                    <div>
                      <label className="mform__label" style={{ display: 'block', marginBottom: 6 }}>{t('directors.lblPhoto')}</label>
                      <label className="btn btn-outline" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Icon name="plus" size={13} /> {t('directors.lblPhoto')}
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhoto} />
                      </label>
                      {form.photoData && (
                        <button type="button" className="btn btn-ghost" style={{ marginInlineStart: 8 }} onClick={() => setForm((p) => ({ ...p, photoData: '' }))}>{t('common.delete')}</button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mform__row mform__row--4">
                  <div className="mform__field mform__field--span2">
                    <label className="mform__label">{t('directors.colMember')} <span>*</span></label>
                    <div className="mform__input-wrap">
                      <Icon name="user" size={14} color="#02c3a6" className="mform__input-icon" />
                      <input className={`mform__input mform__input--with-icon${errors.name ? ' mform__input--error' : ''}`} name="name" value={form.name} onChange={handleChange} />
                    </div>
                    {errors.name && <span className="mform__error">{errors.name}</span>}
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t('directors.lblBranch')} <span>*</span></label>
                    <div className="mform__input-wrap">
                      <Icon name="branch" size={14} color="#1b8eff" className="mform__input-icon" />
                      <select className={`mform__select mform__input--with-icon${errors.branchId ? ' mform__select--error' : ''}`} name="branchId" value={form.branchId} onChange={handleChange}>
                        <option value="">—</option>
                        {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    </div>
                    {errors.branchId && <span className="mform__error">{errors.branchId}</span>}
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t('common.status')}</label>
                    <div className="mform__input-wrap">
                      <Icon name="settings" size={14} color="#14b8a6" className="mform__input-icon" />
                      <select className="mform__select mform__input--with-icon" name="status" value={form.status} onChange={handleChange}>
                        <option value="active">{t('common.active')}</option>
                        <option value="inactive">{t('common.inactive')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mform__row mform__row--4">
                  <div className="mform__field">
                    <label className="mform__label">{t('directors.colNationality')}</label>
                    <div className="mform__input-wrap">
                      <Icon name="report" size={14} color="#8b5cf6" className="mform__input-icon" />
                      <input className="mform__input mform__input--with-icon" name="nationality" value={form.nationality} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t('directors.colNationalId')}</label>
                    <div className="mform__input-wrap">
                      <Icon name="lock" size={14} color="#f59e0b" className="mform__input-icon" />
                      <input className="mform__input mform__input--with-icon" name="nationalId" value={form.nationalId} onChange={handleChange} dir="ltr" />
                    </div>
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t('directors.colBirthDate')}</label>
                    <div className="mform__input-wrap">
                      <input type="date" className="mform__input" name="birthDate" value={form.birthDate} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t('directors.colMembershipType')}</label>
                    <div className="mform__input-wrap">
                      <select className="mform__select" name="membershipType" value={form.membershipType} onChange={handleChange}>
                        <option value="">—</option>
                        {MEMBERSHIP_TYPES.map((m) => <option key={m.value} value={m.value}>{t(m.key)}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mform__row mform__row--4">
                  <div className="mform__field">
                    <label className="mform__label">{t('directors.lblStart')}</label>
                    <div className="mform__input-wrap">
                      <input type="date" className="mform__input" name="membershipStartDate" value={form.membershipStartDate} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t('directors.lblEnd')}</label>
                    <div className="mform__input-wrap">
                      <input type="date" className="mform__input" name="membershipEndDate" value={form.membershipEndDate} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t('directors.colPartnerKind')}</label>
                    <div className="mform__input-wrap">
                      <select className="mform__select" name="partnerKind" value={form.partnerKind} onChange={handleChange}>
                        <option value="">—</option>
                        <option value="شريك">{t('directors.partner')}</option>
                        <option value="مساهم">{t('directors.shareholder')}</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* بيانات التواصل وحساب الدخول */}
                <div style={{ margin: '18px 0 8px', paddingTop: 14, borderTop: '1px dashed #cfd8dc', color: '#607d8b', fontSize: '0.85rem' }}>
                  {t('directors.accountHint')}
                </div>
                <div className="mform__row mform__row--4">
                  <div className="mform__field">
                    <label className="mform__label">{t('common.phone')}</label>
                    <div className="mform__input-wrap">
                      <input className="mform__input" name="phone" value={form.phone} onChange={handleChange} dir="ltr" />
                    </div>
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t('common.email')}</label>
                    <div className="mform__input-wrap">
                      <input className="mform__input" name="email" value={form.email} onChange={handleChange} dir="ltr" />
                    </div>
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t('auth.username', 'اسم المستخدم')}</label>
                    <div className="mform__input-wrap">
                      <input className="mform__input" name="userName" value={form.userName} onChange={handleChange} dir="ltr" autoComplete="off" />
                    </div>
                  </div>
                  <div className="mform__field">
                    <label className="mform__label">{t('auth.password', 'كلمة المرور')}</label>
                    <div className="mform__input-wrap">
                      <input type="password" className="mform__input" name="password" value={form.password} onChange={handleChange} dir="ltr" autoComplete="new-password" />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
